// src/components/legal/MindAttentionToss.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #0064FF;
`;

const Subtitle = styled.p`
    font-size: 1.2rem;
    color: #666;
    margin-bottom: 0.5rem;
`;

const UpdateDate = styled.p`
    color: #999;
    font-size: 0.9rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #0064FF;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const FeatureBox = styled.div`
    background: #F7F9FB;
    border-radius: 12px;
    padding: 2rem;
    margin: 2rem 0;
`;

const FeatureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 1.5rem 0;
`;

const FeatureCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .icon {
        font-size: 2.5rem;
        margin-bottom: 1rem;
    }

    h3 {
        font-size: 1.2rem;
        margin-bottom: 0.5rem;
        color: #333;
    }

    p {
        color: #666;
        font-size: 0.95rem;
    }
`;

const InfoBox = styled.div`
    background: #E8F3FF;
    border-left: 4px solid #0064FF;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const WarningBox = styled.div`
    background: #FFF4E6;
    border-left: 4px solid #FF9800;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const SuccessBox = styled.div`
    background: #E8F8F5;
    border-left: 4px solid #00C896;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const CompanyInfo = styled.div`
    background: #F7F9FB;
    padding: 1.5rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    white-space: pre-wrap;
`;

const PrivacyHighlight = styled.div`
    background: #F0FAF0;
    border: 2px solid #4CAF50;
    border-radius: 12px;
    padding: 2rem;
    margin: 2rem 0;
    text-align: center;
    
    h3 {
        color: #2E7D32;
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }
    
    .highlight {
        font-size: 1.2rem;
        color: #1B5E20;
        font-weight: bold;
        margin: 1rem 0;
    }
`;

const MindAttentionToss = () => {
    return (
        <Container>
            <BackLink to="/mindattention">Mind Attention으로 돌아가기</BackLink>

            <Header>
                <Title>Mind Attention 서비스 이용약관</Title>
                <Subtitle>Mind Attention - 집중력 도구 미니앱</Subtitle>
                <UpdateDate>최종 수정일: 2025년 8월 6일 | 시행일: 2025년 8월 6일</UpdateDate>
            </Header>

            <PrivacyHighlight>
                <h3>🔒 개인정보 최소 수집 안내</h3>
                <div className="highlight">
                    토스로부터 제공받는 정보는 오직 "이름" 하나뿐입니다.
                </div>
                <p style={{marginTop: '1rem', color: '#555'}}>
                    전화번호, 이메일, 생년월일, 계좌정보 등 다른 개인정보는 일체 수집하지 않습니다.
                </p>
            </PrivacyHighlight>

            <InfoBox>
                <strong>📱 토스 미니앱 안내</strong><br/>
                본 서비스는 토스 앱 내에서 제공되는 Mind Attention 미니앱에 대한 이용약관입니다.
                토스 앱에서는 ADHD 집중력 향상을 위한 4가지 핵심 기능만을 제공합니다.
            </InfoBox>

            <Section>
                <SectionTitle>1. Mind Attention 서비스 개요</SectionTitle>
                <Paragraph>
                    마인드월드(이하 "회사")는 Mind Attention 사용자를 위해 Mind Attention의 핵심 기능을 미니앱 형태로 제공합니다.

                    <strong>서비스명:</strong> Mind Attention - 집중력 도구
                    <strong>제공 플랫폼:</strong> 토스 앱 내에 미니앱
                    <strong>서비스 목적:</strong> ADHD 증상 완화 및 집중력 향상 지원
                </Paragraph>
            </Section>

            <FeatureBox>
                <SectionTitle>2. Mind Attention에서 제공되는 기능</SectionTitle>
                <FeatureGrid>
                    <FeatureCard>
                        <div className="icon">⏱️</div>
                        <h3>포모도로 타이머</h3>
                        <p>25분 집중, 5분 휴식의 과학적 시간 관리 기법</p>
                    </FeatureCard>
                    <FeatureCard>
                        <div className="icon">📝</div>
                        <h3>작업 분해</h3>
                        <p>큰 작업을 작은 단위로 나누어 실행력 향상</p>
                    </FeatureCard>
                    <FeatureCard>
                        <div className="icon">🧘</div>
                        <h3>마음챙김</h3>
                        <p>현재 순간에 집중하는 간단한 명상 가이드</p>
                    </FeatureCard>
                    <FeatureCard>
                        <div className="icon">⚙️</div>
                        <h3>환경 설정</h3>
                        <p>집중하기 좋은 환경 만들기 체크리스트</p>
                    </FeatureCard>
                </FeatureGrid>
            </FeatureBox>

            <Section>
                <SectionTitle>3. 이용 조건</SectionTitle>
                <Paragraph>
                    <strong>이용 자격:</strong>
                    • 토스 앱 가입 회원
                    • 만 19세 이상
                    • 대한민국 거주자

                    <strong>계정 연동:</strong>
                    • 토스 계정과 자동 연동
                    • 별도 회원가입 불필요
                    • 토스 탈퇴 시 미니앱 데이터도 함께 삭제
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. 개인정보 수집 및 이용</SectionTitle>

                <WarningBox>
                    <strong>⚠️ 최소 정보 수집 원칙</strong><br/>
                    Mind Attention 토스 미니앱은 토스로부터 <strong style={{color: '#E65100'}}>사용자 이름(실명)</strong>만을 제공받습니다.<br/>
                    전화번호, 이메일, 생년월일, 계좌정보 등 다른 개인정보는 일체 수집하지 않습니다.
                </WarningBox>

                <Paragraph>
                    <strong>📌 토스에서 제공받는 정보 (단 1개):</strong>
                    • <strong style={{color: '#0064FF', fontSize: '1.1rem'}}>사용자 이름</strong>
                    - 제공받는 정보: 토스 계정에 등록된 실명
                    - 사용 목적: 서비스 내 인사말 표시 (예: "안녕하세요, ○○○님")
                    - 저장 방식: 서버에 저장하지 않고 세션 동안만 임시 사용
                    - 보관 기간: 앱 사용 중에만 메모리에 임시 보관, 앱 종료 시 즉시 삭제
                    - 제3자 제공: 절대 없음

                    <strong>❌ 토스에서 제공받지 않는 정보:</strong>
                    • 전화번호 - 수집하지 않음
                    • 이메일 주소 - 수집하지 않음
                    • 생년월일 - 수집하지 않음
                    • 성별 - 수집하지 않음
                    • 주소 - 수집하지 않음
                    • 계좌 정보 - 수집하지 않음
                    • 거래 내역 - 수집하지 않음
                    • 신용 정보 - 수집하지 않음
                    • 기타 모든 금융 정보 - 수집하지 않음

                    <strong>💾 자체 생성 데이터 (로컬 저장):</strong>
                    • 포모도로 타이머 사용 기록 → 기기에만 저장
                    • 작업 목록 → 기기에만 저장
                    • 마음챙김 세션 완료 횟수 → 기기에만 저장
                    • 환경 설정 체크리스트 → 기기에만 저장

                    <strong>🔐 데이터 보안 조치:</strong>
                    • 이름 정보도 암호화하여 처리
                    • 서버에 개인정보 저장하지 않음
                    • 앱 종료 시 메모리에서 완전 삭제
                    • SSL/TLS 암호화 통신만 사용
                    • 로그 파일에도 이름 기록하지 않음
                </Paragraph>

                <SuccessBox>
                    <strong>✅ 사용자 권리 보장</strong><br/>
                    • 이름 표시를 원하지 않으시면 설정에서 "익명 모드" 선택 가능<br/>
                    • 서비스 이용 중 언제든 이름 표시 on/off 가능<br/>
                    • 개인정보 처리에 대한 문의는 즉시 답변 드립니다
                </SuccessBox>
            </Section>

            <Section>
                <SectionTitle>5. 기능별 상세 이용 방법</SectionTitle>
                <Paragraph>
                    <strong>📍 포모도로 타이머:</strong>
                    • 25분 집중 → 5분 휴식 자동 전환
                    • 일시정지/재개 가능
                    • 완료 시 토스 알림 발송
                    • 일일 세션 기록 저장 (로컬)

                    <strong>📍 작업 분해:</strong>
                    • 큰 작업 입력 → AI 자동 분해 제안
                    • 수동으로 세부 작업 추가/수정 가능
                    • 체크리스트 형태로 관리
                    • 완료율 시각화 (로컬 데이터)

                    <strong>📍 마음챙김:</strong>
                    • 3분/5분/10분 세션 선택
                    • 가이드 음성 제공 (선택)
                    • 호흡 가이드 애니메이션
                    • 세션 완료 배지 획득 (로컬)

                    <strong>📍 환경 설정:</strong>
                    • 집중 환경 체크리스트 제공
                    • 개인별 커스터마이징 가능
                    • 환경 점수 측정 (로컬)
                    • 개선 팁 제공
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. 서비스 제한사항</SectionTitle>
                <Paragraph>
                    <strong>토스 미니앱 제한사항:</strong>
                    • CBT 인지 훈련 기능 미제공
                    • 상세 통계 및 분석 기능 미제공
                    • 커뮤니티 기능 미제공
                    • 전문가 상담 연결 미제공

                    <strong>기능 제한:</strong>
                    • 오프라인 사용 불가 (토스 앱 연결 필요)
                    • 데이터 내보내기 기능 없음
                    • 외부 캘린더 연동 불가

                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. 알림 및 권한</SectionTitle>
                <Paragraph>
                    <strong>필요 권한:</strong>
                    • 알림: 포모도로 타이머 종료 알림 (선택)
                    • 저장공간: 작업 목록 로컬 저장 (필수)
                    ※ 카메라, 마이크, 연락처, 위치 등의 권한은 요구하지 않습니다

                    <strong>알림 설정:</strong>
                    • 토스 앱 알림 설정에 따름
                    • 미니앱 내 개별 알림 설정 가능
                    • 방해금지 시간대 설정 가능

                    <strong>권한 거부 시:</strong>
                    • 기본 기능은 모두 사용 가능
                    • 알림 기능만 제한됨
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. 면책 조항</SectionTitle>
                <Paragraph>
                    <strong>의료 서비스 아님:</strong>
                    • 본 서비스는 의료 서비스가 아닙니다
                    • ADHD 진단이나 치료를 제공하지 않습니다
                    • 전문의 상담을 대체할 수 없습니다

                    <strong>서비스 중단:</strong>
                    • 토스 앱 점검 시 서비스 이용 불가
                    • 미니앱 업데이트 시 일시 중단 가능
                    • 사전 공지 후 서비스 종료 가능

                    <strong>책임 제한:</strong>
                    • 서비스 이용으로 인한 직접적 손해만 배상
                    • 간접적, 결과적 손해는 책임지지 않음
                    • 최대 배상액: 0원 (무료 서비스)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. 약관 변경</SectionTitle>
                <Paragraph>
                    <strong>변경 절차:</strong>
                    • 변경 7일 전 토스 앱 내 공지
                    • 중요 변경은 푸시 알림 발송
                    • 변경 후 이용 시 동의로 간주

                    <strong>이용자 권리:</strong>
                    • 변경 약관에 동의하지 않을 권리
                    • 서비스 이용 중단 선택 가능
                    • 기존 데이터 삭제 요청 가능
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. 서비스 종료</SectionTitle>
                <Paragraph>
                    <strong>종료 사유:</strong>
                    • 토스와의 제휴 종료
                    • 서비스 운영 중단 결정
                    • 법적 문제 발생

                    <strong>종료 절차:</strong>
                    • 30일 전 사전 공지
                    • 데이터 백업 방법 안내
                    • Mind Attention 앱 이전 안내

                    <strong>데이터 처리:</strong>
                    • 서비스 종료 시 이름 정보 즉시 삭제
                    • 로컬 데이터는 사용자가 직접 삭제
                    • 익명화된 통계만 보관 (개인 식별 불가)
                </Paragraph>
            </Section>

            <CompanyInfo>
                <SectionTitle>11. 회사 정보 및 연락처</SectionTitle>
                <Paragraph>
                    <strong>회사 정보:</strong>
                    • 회사명: 마인드월드(MindWorld)
                    • 대표자: 윤우재
                    • 주소: 경기도 의왕시 시청1로 11
                    • 사업자등록번호: 504-43-28680

                    <strong>고객 지원:</strong>
                    • 이메일: mind-wellness@naver.com
                    • 토스 앱 내 문의: 설정 → 고객센터 → 제휴 서비스 문의
                    • 응답 시간: 영업일 기준 48시간 이내
                    • 개인정보 관련 문의: 24시간 이내 우선 처리

                    <strong>관련 링크:</strong>
                    • Mind Attention 전체 서비스: <a href="/mindattention">바로가기</a>
                    • 개인정보처리방침: <a href="/mindattention/privacy">바로가기</a>
                    • 일반 이용약관: <a href="/mindattention/terms">바로가기</a>
                </Paragraph>
            </CompanyInfo>

            <SuccessBox>
                <strong>💚 Mind Attention의 약속</strong><br/>
                우리는 ADHD로 어려움을 겪는 분들의 개인정보를 최대한 보호합니다.<br/>
                토스 미니앱은 <strong>이름 하나</strong>만으로 모든 서비스를 제공합니다.<br/>
                여러분의 프라이버시는 우리의 최우선 가치입니다.
            </SuccessBox>

            <Section>
                <SectionTitle>부칙</SectionTitle>
                <Paragraph>
                    1. 본 약관은 2025년 8월 6일부터 시행됩니다.
                    2. 토스 이용약관과 충돌 시 토스 이용약관이 우선 적용됩니다.
                    3. 미니앱 특성상 일부 기능이 예고 없이 변경될 수 있습니다.
                    4. 개인정보 처리 관련 사항은 관련 법령을 준수합니다.
                </Paragraph>
            </Section>
        </Container>
    );
};

export default MindAttentionToss;
